import type { SupabaseClient } from "@supabase/supabase-js";
import { FormField } from "@/lib/database/forms";
import { getFormFileUrl } from "@/lib/database/client/database";
import { formatValueForExport } from "@/features/forms/_logic/export-value-formatter";
import { isAnswerableField } from "./field-types";
import { PHONE_KEYWORDS } from "./phone-utils";

export interface FormFieldAvailability {
  hasEmail: boolean;
  hasName: boolean;
  hasPhone: boolean;
}

const NAME_KEYWORDS = ["first name", "last name", "full name", "first_name", "last_name", "full_name"];

/** Check if a label contains any of the keywords (handles labels with extra info like "First Name (required)") */
function labelMatchesAny(label: string, keywords: string[]): boolean {
  return keywords.some((keyword) => label.includes(keyword));
}

/** Detect which fields (email, name, phone) are available based on form field labels and types */
export function detectFieldAvailability(fields: FormField[]): FormFieldAvailability {
  const labels = fields.map((f) => f.label.toLowerCase());
  const types = fields.map((f) => f.type.toLowerCase());

  const hasEmail = types.includes("email") || labels.some((l) => l.includes("email"));
  const hasName = labels.some((l) => l.includes("name") || labelMatchesAny(l, NAME_KEYWORDS));
  const hasPhone = types.includes("tel") || labels.some((l) => labelMatchesAny(l, PHONE_KEYWORDS));

  return { hasEmail, hasName, hasPhone };
}

// Word-boundary matchers for phone/email labels. Unlike the substring heuristic
// in detectFieldAvailability (which only toggles capability flags), redaction
// drops the value, so over-matching means silent data loss — these boundaries
// keep "Hotel" (tel), "cancellation" (cell), and "Tell us…" from being stripped.
const PHONE_LABEL_RE = /\b(phone|mobile|cell|telephone|tel)\b/i;
const EMAIL_LABEL_RE = /\be-?mail\b/i;

/**
 * Returns the labels of fields that hold phone or email PII — by field type
 * (`tel`/`email`) or by a word-boundary label match, so phone/email-labeled text
 * fields count too. Used to strip sensitive values before an anonymous form draft
 * is written to localStorage as clear text (authenticated drafts go to the
 * access-controlled DB and keep the full data).
 *
 * @param fields - Form field definitions
 * @returns Field labels considered sensitive (phone or email)
 */
export function getSensitiveDraftKeys(fields: FormField[]): string[] {
  return fields
    .filter((field) => {
      const type = field.type.toLowerCase();
      const isPhone = type === "tel" || PHONE_LABEL_RE.test(field.label);
      const isEmail = type === "email" || EMAIL_LABEL_RE.test(field.label);
      return isPhone || isEmail;
    })
    .map((field) => field.label);
}

/**
 * Field label -> current type, for stamping into a saved draft alongside its answers (see
 * `dropStaleTypedDraftValues`) so a restored draft can tell whether a field's type has
 * changed since the value was captured.
 *
 * @param fields - Form field definitions
 * @returns Type keyed by label
 */
export function getFieldTypesByLabel(fields: FormField[]): Record<string, string> {
  const typesByLabel: Record<string, string> = {};
  for (const field of fields) {
    if (field.label && isAnswerableField(field)) typesByLabel[field.label] = field.type;
  }
  return typesByLabel;
}

/** Returns the first whitespace-delimited token of a name, or null when empty. */
export function parseFirstName(name: string | null | undefined): string | null {
  if (typeof name !== "string") return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  const [first] = trimmed.split(/\s+/);
  return first || null;
}

/**
 * Finds the recipient's first name by scanning form responses for a name-shaped field.
 *
 * Priority: exact "first name" match → any label containing "first name" → "full name" →
 * a label that ends in "name" or is exactly "name".
 *
 * @param fields - Form field definitions (used to find a name-shaped field by label)
 * @param responseData - The submitted response, keyed by field id
 * @returns First name string when found, otherwise null
 */
export function extractRecipientFirstName(
  fields: FormField[],
  responseData: Record<string, unknown> | null | undefined
): string | null {
  if (!responseData || typeof responseData !== "object") return null;

  const candidates: Array<(label: string) => boolean> = [
    (l) => l === "first name",
    (l) => l.includes("first name") || l.includes("first_name"),
    (l) => l === "full name" || l.includes("full name") || l.includes("full_name"),
    (l) => l === "name" || l.endsWith(" name") || l.startsWith("your name"),
  ];

  for (const matches of candidates) {
    const field = fields.find((f) => matches(f.label.toLowerCase()));
    if (!field) continue;
    const value = responseData[field.id];
    const firstName = parseFirstName(typeof value === "string" ? value : null);
    if (firstName) return firstName;
  }

  return null;
}

/** Returns the trimmed string value of the first field whose label matches, or null. */
function findFieldValue(
  fields: FormField[],
  responseData: Record<string, unknown>,
  matches: (label: string) => boolean
): string | null {
  const field = fields.find((f) => matches(f.label.toLowerCase()));
  if (!field) return null;
  const value = responseData[field.id];
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

// Shared name-field label predicates (lowercase label in). Exported so the email
// variable resolver (file-delivery-email-content) and filename builder use one heuristic.

/** Matches a dedicated full-name / "name" field, excluding first/last (which also end in " name"). */
export const isFullNameLabel = (label: string): boolean =>
  label.includes("full name") ||
  label.includes("full_name") ||
  ((label === "name" || label.endsWith(" name") || label.startsWith("your name")) &&
    !label.includes("first") &&
    !label.includes("last"));

/** Matches a first-name field. */
export const isFirstNameLabel = (label: string): boolean =>
  label === "first name" || label.includes("first name") || label.includes("first_name");

/** Matches a last-name field. */
export const isLastNameLabel = (label: string): boolean =>
  label === "last name" || label.includes("last name") || label.includes("last_name");

/**
 * Finds the recipient's full name by scanning form responses for name-shaped fields.
 *
 * Priority: a dedicated full/your/"name" field → otherwise the first name and last
 * name fields combined. Unlike {@link extractRecipientFirstName}, this returns the
 * whole name (e.g. "Jane Doe"), suitable for building human-facing filenames.
 *
 * @param fields - Form field definitions (used to find name-shaped fields by label)
 * @param responseData - The submitted response, keyed by field id
 * @returns Full name string when found, otherwise null
 */
export function extractRecipientName(
  fields: FormField[],
  responseData: Record<string, unknown> | null | undefined
): string | null {
  if (!responseData || typeof responseData !== "object") return null;

  const fullName = findFieldValue(fields, responseData, isFullNameLabel);
  if (fullName) return fullName;

  const first = findFieldValue(fields, responseData, isFirstNameLabel);
  const last = findFieldValue(fields, responseData, isLastNameLabel);
  const combined = [first, last].filter(Boolean).join(" ");
  return combined || null;
}

/** Cap on a single form-field answer's length once formatted for an email — bounds both the memory
 *  cost of holding every recipient's answers for a bulk send and how much a long free-text answer
 *  can bloat an email body. */
const MAX_ANSWER_VARIABLE_LENGTH = 500;

/**
 * Resolves a field's answer from `response_data`, tolerating the codebase's existing id-vs-label
 * keying inconsistency (a real submission is keyed by `field.id`; older sample/test data by
 * `field.label` — see `file-delivery-email-content.ts`). Pinned to exactly these two keys, in this
 * order — never any other key, so this can't become an arbitrary lookup.
 *
 * @param responseData - The response's raw `response_data` JSONB.
 * @param field - The field whose answer to resolve.
 * @returns The raw answer value (any JSON shape), or undefined if unanswered.
 */
export function getFormFieldAnswer(
  responseData: Record<string, unknown> | null | undefined,
  field: FormField
): unknown {
  if (!responseData) return undefined;
  return responseData[field.id] ?? responseData[field.label];
}

/**
 * Formats a field's answer for inline display in an email — the value inserted for a
 * `form_field_*` merge variable. File/signature answers resolve to their storage URL
 * (`getFormFileUrl` is a synchronous `getPublicUrl`, not a network call); everything else uses the
 * same flattening rules as CSV/Excel export. Truncated so one long free-text answer can't bloat an
 * email. Returned RAW (not HTML-escaped) — escaping is applied at the render sink, same contract as
 * every other email variable (see variable-resolution.ts).
 *
 * @param supabase - Client used to resolve a file/signature answer's storage URL.
 * @param value - The raw answer value (see {@link getFormFieldAnswer}).
 * @param field - The field the value answers (selects file-URL vs. plain formatting).
 * @returns The answer as a display string; empty string when unanswered.
 */
export function formatFormFieldAnswerForEmail(supabase: SupabaseClient, value: unknown, field: FormField): string {
  const formatted =
    (field.type === "file" || field.type === "signature") && typeof value === "string" && value
      ? getFormFileUrl(supabase, value)
      : formatValueForExport(value);
  return formatted.length > MAX_ANSWER_VARIABLE_LENGTH
    ? `${formatted.slice(0, MAX_ANSWER_VARIABLE_LENGTH)}…`
    : formatted;
}
